import { useEffect, useState, type ReactNode } from 'react'
import { Video } from 'lucide-react'

import type { AnimalDetail } from '@cattle/contracts'
import { teethLabel } from '@cattle/money'

import { Drawer, ErrorState, KeyValue, LoadingSkeleton, Money } from '../../components'
import { useBookAnimal } from '../../fixtures'
import { fetchMediaObjectUrl, MediaFetchError } from '../../lib/apiClient'
import { cn } from '../../lib/cn'
import { formatDate } from '../../lib/format'
import { t } from '../../lib/i18n'

/**
 * P-04 · one animal of an opened book, in full.
 *
 * The owner asked for everything the trader recorded to be readable here (11
 * Sept 2026) — photos, tag, type, breed, teeth, weights, where and from whom it
 * was bought, what it has cost and what it sold for. It is the trader's own
 * `GET /animals/:id`, read through the grant's scoped token, so it shows exactly
 * what his phone shows and nothing the grant does not allow.
 */

/**
 * An `<img>` for a protected media variant. The bytes are fetched with the
 * grant's token and shown from a `blob:` URL, which is revoked on unmount.
 *
 * A fetch that fails is still not worth an error banner over the rest of the
 * animal — but it must not look like an animal that was never photographed
 * either. That was the old behaviour: a 401, a 404 and a photo still arriving
 * all rendered the identical grey box, so the only way to tell them apart was
 * to read this file. Now the failure is a state, the status code is on the
 * element as `data-media-status`, and the CALLER decides what a failure looks
 * like — a dense table row wants its tag chip back, the drawer wants to say so.
 */
export function GrantImage({
  mediaId,
  variant,
  token,
  alt,
  className,
  fallback,
}: {
  mediaId: string
  variant: string
  /** Absent when the grant carries none — the placeholder stays. */
  token: string | undefined
  alt: string
  className?: string
  /** Drawn instead of the image once the fetch has failed. */
  fallback?: ReactNode
}) {
  const [src, setSrc] = useState<string | null>(null)
  const [failedStatus, setFailedStatus] = useState<number | null>(null)

  useEffect(() => {
    if (!token) return
    let live = true
    let url: string | null = null
    setFailedStatus(null)
    fetchMediaObjectUrl(mediaId, variant, token).then(
      (objectUrl) => {
        if (live) {
          url = objectUrl
          setSrc(objectUrl)
        } else {
          URL.revokeObjectURL(objectUrl)
        }
      },
      (error: unknown) => {
        if (live) setFailedStatus(error instanceof MediaFetchError ? error.status : 0)
      },
    )
    return () => {
      live = false
      if (url) URL.revokeObjectURL(url)
      setSrc(null)
    }
  }, [mediaId, variant, token])

  if (src) return <img src={src} alt={alt} className={cn('object-cover', className)} />

  if (failedStatus !== null) {
    /* `display: contents` keeps the status readable from the DOM without
       putting a box around a fallback that already has its own. */
    if (fallback !== undefined) {
      return (
        <span data-media-status={failedStatus} className="contents">
          {fallback}
        </span>
      )
    }
    return (
      <span
        data-media-status={failedStatus}
        title={t('p.inside.mediaUnavailableCode', { status: failedStatus })}
        className={cn(
          'flex items-center justify-center bg-danger/10 px-1.5 text-center text-[10.5px] font-bold leading-tight text-danger',
          className,
        )}
      >
        {t('p.inside.mediaUnavailable')}
      </span>
    )
  }

  /* Still in flight. A pulse says "coming", where a flat box said "nothing". */
  return <span aria-hidden="true" className={cn('block animate-pulse bg-accent-glass', className)} />
}

function Heading({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-1 mt-5 font-display text-[13px] font-bold uppercase tracking-[0.6px] text-muted first:mt-0">
      {children}
    </h3>
  )
}

const none = () => <span className="text-muted">{t('common.none')}</span>
const text = (value: string | null | undefined) => (value ? value : none())
const date = (value: string | null | undefined) => (value ? formatDate(value) : none())
const kg = (value: number | null | undefined) => (value ? `${value} kg` : none())

/**
 * What `status` actually means, and why only one value hides a file.
 *
 * `FAILED` does NOT mean the photograph is gone. `media.worker.ts` settles a row
 * `FAILED` when any one derivation job failed — and its own comment says the
 * original "is still in storage and still served", because ARCH §5 is explicit
 * that a missing derived variant is not a failed upload. `openVariant` proves
 * it: it refuses `UPLOADING` and nothing else, and falls back to `original`
 * for every variant it cannot find.
 *
 * So this console used to be the ONLY client that threw those photographs away.
 * The trader's own phone draws them (`AnimalCard.tsx`) and the owner, looking at
 * the same animal on two screens, saw pictures on one and grey boxes on the
 * other. With no ffmpeg on the host every video over the transcode threshold
 * settles `FAILED`, which is most of them.
 *
 * `UPLOADING` is the one real absence: the bytes have not landed, and the server
 * 404s it.
 */
function isServable(status: string): boolean {
  return status !== 'UPLOADING'
}

export function Photos({ animal, token }: { animal: AnimalDetail; token: string | undefined }) {
  const media = animal.media.filter((item) => isServable(item.status))
  if (media.length === 0) {
    return <p className="text-[13px] text-muted">{t('p.inside.noPhotos')}</p>
  }

  return (
    <div className="grid grid-cols-2 gap-2.5">
      {media.map((item) => {
        /* Ask for a derived variant only when the row says it has one. The
           server would fall back anyway, but asking for what exists saves a
           storage stat per tile and keeps the request honest. A video with no
           poster cannot go in an `<img>` at all — that is the one null. */
        const variant =
          item.kind === 'VIDEO'
            ? item.variants.includes('poster')
              ? 'poster'
              : null
            : item.variants.includes('display')
              ? 'display'
              : 'original'
        return (
          <figure
            key={item.id}
            className="relative overflow-hidden rounded-[12px] border border-glass-border"
          >
            {variant ? (
              <GrantImage
                mediaId={item.id}
                variant={variant}
                token={token}
                alt={item.caption ?? animal.nickname ?? t('animal.one')}
                className="aspect-[4/3] w-full"
              />
            ) : (
              <span className="flex aspect-[4/3] w-full items-center justify-center bg-accent-glass text-accent-dark">
                <Video className="size-6" />
              </span>
            )}
            {item.kind === 'VIDEO' ? (
              <span className="absolute start-2 top-2 rounded-full bg-dark/70 px-2 py-0.5 text-[11px] font-bold text-white">
                {t('p.inside.video')}
              </span>
            ) : null}
            {item.isCover ? (
              <span className="absolute end-2 top-2 rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-white">
                {t('p.inside.cover')}
              </span>
            ) : null}
            {/* Shown, and said. A file whose derivation failed is still the
                trader's photograph; hiding the fact would make a broken
                pipeline look healthy, and hiding the file made it look like he
                never took one. System health already lists the failed jobs. */}
            {item.status === 'FAILED' ? (
              <span
                title={item.failureReason ?? undefined}
                className="absolute bottom-2 start-2 rounded-full bg-warn/90 px-2 py-0.5 text-[11px] font-bold text-white"
              >
                {t('p.inside.originalOnly')}
              </span>
            ) : item.status === 'PROCESSING' ? (
              <span className="absolute bottom-2 start-2 rounded-full bg-dark/70 px-2 py-0.5 text-[11px] font-bold text-white">
                {t('p.inside.stillProcessing')}
              </span>
            ) : null}
          </figure>
        )
      })}
    </div>
  )
}

function Details({ animal, token }: { animal: AnimalDetail; token: string | undefined }) {
  const cost = animal.costBreakdown
  const sale = animal.sale

  return (
    <>
      <Heading>{t('animal.photos')}</Heading>
      <Photos animal={animal} token={token} />

      <Heading>{t('p.inside.sectionAnimal')}</Heading>
      <KeyValue label={t('animal.tagNumber')} value={text(animal.tagNumber)} />
      <KeyValue label={t('animal.nickname')} value={text(animal.nickname)} />
      <KeyValue label={t('animal.status')} value={t(`animalStatus.${animal.status}`)} />
      <KeyValue label={t('animal.sex')} value={t(`sex.${animal.sex}`)} />
      <KeyValue
        label={t('animal.breed')}
        value={animal.breed ? t(`breed.${animal.breed}`) : none()}
      />
      <KeyValue label={t('animal.teeth')} value={teethLabel(animal.teethAtPurchase)} />
      <KeyValue label={t('animal.colour')} value={text(animal.colour)} />
      <KeyValue label={t('animal.markings')} value={text(animal.markings)} />
      <KeyValue label={t('animal.weightIn')} value={kg(animal.weightInKg)} />
      <KeyValue label={t('animal.weightOut')} value={kg(animal.weightOutKg)} />
      <KeyValue label={t('p.inside.lot')} value={text(animal.lotName)} />
      <KeyValue label={t('p.inside.season')} value={text(animal.seasonTag)} />
      <KeyValue label={t('animal.notes')} value={text(animal.notes)} />

      <Heading>{t('p.inside.sectionPurchase')}</Heading>
      <KeyValue label={t('animal.purchaseDate')} value={date(animal.purchaseDate)} />
      <KeyValue label={t('animal.seller')} value={text(animal.sellerName)} />
      <KeyValue label={t('animal.mandi')} value={text(animal.purchaseMandi)} />
      <KeyValue
        label={t('animal.purchasePrice')}
        value={<Money paisa={cost?.purchasePricePaisa} />}
      />

      <Heading>{t('p.inside.sectionCost')}</Heading>
      <KeyValue label={t('p.inside.daysHeld')} value={String(animal.daysHeld)} />
      {cost?.lines.map((line) => (
        <KeyValue
          key={line.key}
          label={line.sub ? `${line.label} · ${line.sub}` : line.label}
          value={<Money paisa={line.amountPaisa} />}
        />
      ))}
      <KeyValue label={t('p.inside.totalCost')} value={<Money paisa={cost?.totalPaisa} />} />
      <KeyValue label={t('p.inside.dailyBurn')} value={<Money paisa={animal.dailyBurnPaisa} />} />
      {animal.status === 'SOLD' ? null : (
        <>
          <KeyValue
            label={t('p.inside.colBreakEven')}
            value={<Money paisa={animal.breakEvenPaisa} />}
          />
          <KeyValue
            label={t('animal.askingPrice')}
            value={<Money paisa={animal.askingPricePaisa} />}
          />
        </>
      )}

      {sale ? (
        <>
          <Heading>{t('p.inside.sectionSale')}</Heading>
          <KeyValue label={t('p.inside.saleDate')} value={date(sale.saleDate)} />
          <KeyValue label={t('p.inside.buyer')} value={text(sale.buyerName)} />
          <KeyValue label={t('p.inside.salePrice')} value={<Money paisa={sale.salePricePaisa} />} />
          <KeyValue
            label={t('p.inside.commission')}
            value={<Money paisa={sale.commissionPaisa} />}
          />
          <KeyValue
            label={t('p.inside.profit')}
            value={<Money paisa={sale.profitPaisa} signed tone="positive" />}
          />
          <KeyValue
            label={t('p.inside.outstanding')}
            value={<Money paisa={sale.outstandingPaisa} tone="warn" />}
          />
        </>
      ) : null}

      {animal.status === 'DIED' || animal.status === 'LOST' ? (
        <>
          <Heading>{t('p.inside.sectionExit')}</Heading>
          <KeyValue label={t('p.inside.exitDate')} value={date(animal.exitDate)} />
          <KeyValue label={t('p.inside.deathCause')} value={text(animal.deathCause)} />
          <KeyValue label={t('p.inside.exitLoss')} value={<Money paisa={animal.exitLossPaisa} />} />
        </>
      ) : null}

      {animal.weights.length > 0 ? (
        <>
          <Heading>{t('p.inside.sectionWeights')}</Heading>
          {animal.weights.map((entry) => (
            <KeyValue
              key={entry.id}
              label={formatDate(entry.weighedOn)}
              value={kg(entry.weightKg)}
            />
          ))}
        </>
      ) : null}
    </>
  )
}

export function BookAnimalDrawer({
  slug,
  animalId,
  token,
  onClose,
}: {
  slug: string
  animalId: string | null
  token: string | undefined
  onClose: () => void
}) {
  const animal = useBookAnimal(slug, animalId)
  const title = animal.data
    ? (animal.data.nickname ?? animal.data.tagNumber ?? t('animal.one'))
    : t('p.inside.animalDetails')

  return (
    <Drawer
      open={animalId !== null}
      onClose={onClose}
      title={title}
      subtitle={
        animal.data?.tagNumber ? t('p.inside.tagNumber', { tag: animal.data.tagNumber }) : undefined
      }
    >
      {animal.isError && animal.error ? (
        <ErrorState error={animal.error} onRetry={() => void animal.refetch()} />
      ) : !animal.data ? (
        <LoadingSkeleton variant="panel" />
      ) : (
        <Details animal={animal.data} token={token} />
      )}
    </Drawer>
  )
}
